import React, { useState, useEffect } from 'react';
import { 
  FaSearch, 
  FaEllipsisV, 
  FaPaperPlane, 
  FaImage,
  FaSmile,
  FaMicrophone,
  FaSpinner
} from 'react-icons/fa';
import { useAuth } from '../context/AuthContext';
import AppBar from '../components/AppBar';
import { chatAPI } from '../services/api';
import toast from 'react-hot-toast';

const ChatScreen = () => {
  const { user } = useAuth();
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [conversationType, setConversationType] = useState('inbox');
  const [loading, setLoading] = useState(true);
  const [sendingMessage, setSendingMessage] = useState(false);

  useEffect(() => {
    fetchConversations();
  }, [conversationType]);

  useEffect(() => {
    if (selectedConversation) {
      fetchMessages(selectedConversation.id);
    }
  }, [selectedConversation]);

  const fetchConversations = async () => {
    try {
      setLoading(true);
      const response = await chatAPI.getConversations(conversationType);
      console.log('💬 Conversations fetched:', response);
      setConversations(response || []);
    } catch (error) {
      console.error('Error fetching conversations:', error);
      toast.error('Failed to load conversations');
    } finally {
      setLoading(false);
    }
  };

  const fetchMessages = async (conversationId) => {
    try {
      const response = await chatAPI.getMessages(conversationId);
      console.log('💬 Messages fetched:', response);
      setMessages(response || []);
    } catch (error) {
      console.error('Error fetching messages:', error);
      toast.error('Failed to load messages');
    }
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim() || !selectedConversation) return;

    try {
      setSendingMessage(true);
      await chatAPI.sendMessage(selectedConversation.id, newMessage.trim());
      
      // Refresh messages after sending
      await fetchMessages(selectedConversation.id);
      
      setNewMessage('');
      toast.success('Message sent!');
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error('Failed to send message');
    } finally {
      setSendingMessage(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const getOtherUser = (conversation) => {
    if (!conversation.users || !user) return null;
    return conversation.users.find(u => u.id !== user.id);
  };

  const filteredConversations = conversations.filter(conversation => {
    if (!searchQuery) return true;
    const otherUser = getOtherUser(conversation);
    if (!otherUser) return false;
    
    const searchLower = searchQuery.toLowerCase();
    return (
      otherUser.meta?.name?.toLowerCase().includes(searchLower) ||
      otherUser.username?.toLowerCase().includes(searchLower)
    );
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-weloop-dark">
        <AppBar />
        <div className="flex justify-center items-center py-20">
          <div className="flex items-center gap-3 text-gray-500">
            <FaSpinner className="animate-spin" />
            Loading conversations...
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-weloop-dark">
      <AppBar />
      
      <div className="flex h-[calc(100vh-80px)]">
                 {/* Chat List */}
         <div className="w-80 bg-weloop-gray/20 border-r border-weloop-gray-lighter flex flex-col">
           {/* Header */}
           <div className="p-4 border-b border-weloop-gray-lighter">
             <h1 className="text-xl font-bold text-white">Messages</h1>
           </div>

           {/* Type Filter */}
           <div className="p-4 border-b border-weloop-gray-lighter">
             <div className="flex gap-2">
               <button
                 onClick={() => setConversationType('inbox')}
                 className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                   conversationType === 'inbox'
                     ? 'bg-weloop-green text-white'
                     : 'bg-weloop-gray text-gray-300 hover:bg-weloop-gray-light'
                 }`}
               >
                 Inbox
               </button>
               <button
                 onClick={() => setConversationType('groups')}
                 className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                   conversationType === 'groups'
                     ? 'bg-weloop-green text-white'
                     : 'bg-weloop-gray text-gray-300 hover:bg-weloop-gray-light'
                 }`}
               >
                 Groups
               </button>
             </div>
           </div>

          {/* Search */}
          <div className="p-4 border-b border-weloop-gray-lighter">
            <div className="relative">
              <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 w-4 h-4" />
              <input
                type="text"
                placeholder="Search conversations..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 bg-weloop-gray border border-weloop-gray-lighter rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-weloop-green"
              />
            </div>
          </div>

          {/* Conversations List */}
          <div className="flex-1 overflow-y-auto">
            {filteredConversations.length === 0 ? (
              <div className="p-4 text-center text-gray-500">
                <div className="text-sm">No conversations found</div>
              </div>
            ) : (
              filteredConversations.map((conversation) => {
                const otherUser = getOtherUser(conversation);
                const latestMessage = conversation.latest_message;
                
                return (
                  <div
                    key={conversation.id}
                    onClick={() => setSelectedConversation(conversation)}
                    className={`p-4 border-b border-weloop-gray-lighter cursor-pointer transition-colors ${
                      selectedConversation?.id === conversation.id
                        ? 'bg-weloop-green/20'
                        : 'hover:bg-weloop-gray/30'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      {/* Avatar */}
                      <img
                        src={otherUser?.meta?.profile_image || '/default-avatar.png'}
                        alt={otherUser?.meta?.name || otherUser?.username}
                        className="w-12 h-12 rounded-full object-cover"
                      />
                      
                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-white font-semibold text-sm truncate">
                            {otherUser?.meta?.name || otherUser?.username || 'Unknown User'}
                          </span>
                          {latestMessage?.timeago && (
                            <span className="text-gray-500 text-xs">
                              {latestMessage.timeago}
                            </span>
                          )}
                        </div>
                        
                        {latestMessage && (
                          <div className="text-gray-400 text-sm truncate">
                            {latestMessage.message}
                          </div>
                        )}
                        
                        {conversation.count_messages > 0 && (
                          <div className="text-gray-500 text-xs mt-1">
                            {conversation.count_messages} messages
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>

        {/* Chat Area */}
        <div className="flex-1 flex flex-col">
          {selectedConversation ? (
            <>
              {/* Chat Header */}
              <div className="p-4 border-b border-weloop-gray-lighter bg-weloop-gray/20">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <img
                      src={getOtherUser(selectedConversation)?.meta?.profile_image || '/default-avatar.png'}
                      alt={getOtherUser(selectedConversation)?.meta?.name || getOtherUser(selectedConversation)?.username}
                      className="w-10 h-10 rounded-full object-cover"
                    />
                    <div>
                      <div className="text-white font-semibold">
                        {getOtherUser(selectedConversation)?.meta?.name || getOtherUser(selectedConversation)?.username}
                      </div>
                      <div className="text-gray-500 text-sm">
                        {selectedConversation.count_messages} messages
                      </div>
                    </div>
                  </div>
                  <button className="p-2 text-gray-500 hover:text-white transition-colors">
                    <FaEllipsisV className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {/* Messages */}
              <div className="flex-1 overflow-y-auto p-4 space-y-4">
                {messages.length === 0 ? (
                  <div className="text-center text-gray-500 py-8">
                    <div className="text-sm">No messages yet</div>
                    <div className="text-xs mt-1">Start the conversation!</div>
                  </div>
                ) : (
                  messages.map((message) => {
                    const isOwnMessage = message.user_id === user?.id;
                    
                    return (
                      <div
                        key={message.id}
                        className={`flex ${isOwnMessage ? 'justify-end' : 'justify-start'}`}
                      >
                        <div className={`max-w-xs lg:max-w-md px-4 py-2 rounded-lg ${
                          isOwnMessage
                            ? 'bg-weloop-green text-white'
                            : 'bg-weloop-gray text-white'
                        }`}>
                          <div className="text-sm">{message.message}</div>
                          <div className={`text-xs mt-1 ${
                            isOwnMessage ? 'text-green-100' : 'text-gray-400'
                          }`}>
                            {message.timeago}
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>

              {/* Message Input */}
              <div className="p-4 border-t border-weloop-gray-lighter bg-weloop-gray/20">
                <div className="flex items-center gap-3">
                  <button className="p-2 text-gray-500 hover:text-white transition-colors">
                    <FaImage className="w-4 h-4" />
                  </button>
                  <button className="p-2 text-gray-500 hover:text-white transition-colors">
                    <FaSmile className="w-4 h-4" />
                  </button>
                  
                  <div className="flex-1 relative">
                    <input
                      type="text"
                      placeholder="Type a message..."
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      onKeyPress={handleKeyPress}
                      className="w-full px-4 py-2 bg-weloop-gray border border-weloop-gray-lighter rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-weloop-green"
                    />
                  </div>
                  
                  <button
                    onClick={handleSendMessage}
                    disabled={!newMessage.trim() || sendingMessage}
                    className="p-2 bg-weloop-green hover:bg-weloop-green-dark text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {sendingMessage ? (
                      <FaSpinner className="w-4 h-4 animate-spin" />
                    ) : (
                      <FaPaperPlane className="w-4 h-4" />
                    )}
                  </button>
                  
                  <button className="p-2 text-gray-500 hover:text-white transition-colors">
                    <FaMicrophone className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center text-gray-500">
                <div className="text-lg font-semibold text-white mb-2">
                  Select a conversation
                </div>
                <div className="text-sm">
                  Choose a conversation from the list to start messaging
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ChatScreen; 